import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  pointerWithin,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { ReactComponent as TrashClosedIcon } from './trash_closed.svg';
import { ReactComponent as TrashOpenIcon } from './trash_opened.svg';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { groupByPosition, orderByPosition } from '../orderByPosition';

// Temp ids for components that haven't been persisted yet. Deeply negative
// so they can't collide with real auto-incremented DB ids OR the reserved
// service-log component (id -1), and small enough magnitude to survive
// `parseInt` in the drag/reorder code. The server's `temp_component_id?`
// helper treats any id <= -1000 (or the legacy 'new' sentinel) as a create
// request and returns the real id back via `id_remap` on the response.
let __nextTempComponentId = -1000;
const generateTempComponentId = () => --__nextTempComponentId; // first call returns -1001
const isUserEditableComponent = (c) => Math.abs(c.id) > 1; // excludes reserved 0/1/-1

// Sortable id helpers. We namespace component ids vs group ids so a
// single DndContext can sort both kinds without collisions.
const COMP_PREFIX = 'comp:';
const GROUP_PREFIX = 'group:';
const DELETE_ZONE_ID = 'delete-zone';
const UNGROUPED = 'ungrouped';
const compSortableId = (id) => `${COMP_PREFIX}${id}`;
const groupSortableId = (group) => `${GROUP_PREFIX}${group}`;
const parseSortableId = (id) => {
  if (typeof id !== 'string') return null;
  if (id.startsWith(COMP_PREFIX)) return { type: 'component', id: parseInt(id.slice(COMP_PREFIX.length), 10) };
  if (id.startsWith(GROUP_PREFIX)) return { type: 'group', group: id.slice(GROUP_PREFIX.length) };
  return null;
};
const groupLabel = (group) => (group === UNGROUPED ? 'Ungrouped' : group);

// useSortable applies `transition` as an inline style (often `transform 0ms`
// when its items array is a new reference). That overrides stylesheet
// transitions, so visual properties have to ride along on the same string.
const ITEM_VISUAL_TRANSITION =
  'background-color .22s ease, color .22s ease, box-shadow .28s cubic-bezier(0.32, 0.72, 0, 1)';
const GROUP_VISUAL_TRANSITION =
  'background-color .15s ease, border-color .15s ease';
const composeDndStyle = (transform, dndTransition, visualTransition, extra = {}) => ({
  transform: CSS.Transform.toString(transform),
  transition: [dndTransition, visualTransition].filter(Boolean).join(', '),
  ...extra,
});

function SortableComponentItem({
  component,
  isVisible,
  isActiveSelection,
  showSavedIndicator,
  editComponentName,
  setEditComponentName,
  setConfirmDelete,
  handleNameChange,
  submitNameChange,
  cancelNameChange,
  toggleVisibility,
  onSelectComponent,
  locked,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: compSortableId(component.id),
    data: { type: 'component', componentId: component.id, group: component.group },
  });

  const style = composeDndStyle(transform, transition, ITEM_VISUAL_TRANSITION, {
    opacity: isDragging ? 0.4 : 1,
  });

  const isEditing = editComponentName === component.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        'rc-item',
        isDragging ? 'dragging' : '',
        isVisible ? '' : 'is-hidden',
        isActiveSelection ? 'is-active' : '',
        isEditing ? 'is-editing' : '',
      ].filter(Boolean).join(' ')}
      data-id={component.id}
      onClick={(e) => {
        e.stopPropagation();
        onSelectComponent?.(component.id);
      }}
    >
      {!component.synced && <div className="saving-flash shown" />}

      <div className={`saved-indicator ${showSavedIndicator ? 'shown' : ''}`}>
        <CheckIcon style={{ fontSize: 14 }} />
      </div>

      {!locked && (
        <button
          type="button"
          className="rc-handle"
          aria-label="Reorder component"
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIndicatorIcon />
        </button>
      )}

      {isEditing && !locked ? (
        <>
          <input
            className="rc-item-input browser-default"
            type="text"
            value={component.name}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleNameChange(component.id, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitNameChange();
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                cancelNameChange(component.id);
              }
            }}
          />

          <button
            type="button"
            className="rc-icon-button is-confirm"
            aria-label="Save name"
            onClick={(e) => {
              e.stopPropagation();
              submitNameChange();
            }}
          >
            <CheckRoundedIcon />
          </button>

          <button
            type="button"
            className="rc-icon-button is-danger"
            aria-label="Remove system from this report"
            onClick={(e) => {
              e.stopPropagation();
              setConfirmDelete({
                id: component.id,
                name: component.name,
                type: 'component',
              });
            }}
          >
            <DeleteOutlineRoundedIcon />
          </button>
        </>
      ) : (
        <>
          <span className="rc-item-name">{component.name}</span>

          {!locked && <button
            type="button"
            className="rc-icon-button"
            aria-label={`Rename ${component.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setEditComponentName(component.id);
            }}
          >
            <EditOutlinedIcon />
          </button>}

          {!locked && <button
            type="button"
            className={`rc-icon-button is-toggle${isVisible ? '' : ' is-off'}`}
            aria-label={isVisible ? `Hide ${component.name}` : `Show ${component.name}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility(component.id, 'component');
            }}
          >
            {isVisible ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
          </button>}
        </>
      )}
    </div>
  );
}

function SortableGroupContainer({
  group,
  count,
  firstComponentId,
  isGroupVisible,
  isActiveSelection,
  toggleVisibility,
  onSelectComponent,
  locked,
  children,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: groupSortableId(group),
    data: { type: 'group', group },
  });

  const style = composeDndStyle(transform, transition, GROUP_VISUAL_TRANSITION, {
    opacity: isDragging ? 0.6 : 1,
  });

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={[
        'rc-group',
        isGroupVisible ? '' : 'is-hidden',
        isActiveSelection ? 'is-active' : '',
        isOver ? 'is-over' : '',
      ].filter(Boolean).join(' ')}
      data-id={group}
    >
      <div
        className="rc-group-head"
        onClick={() => {
          if (firstComponentId !== undefined) onSelectComponent?.(firstComponentId);
        }}
      >
        {!locked && (
          <button
            type="button"
            className="rc-handle"
            aria-label="Reorder group"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon />
          </button>
        )}

        <span className="rc-group-name">{groupLabel(group)}</span>
        <span className="rc-group-count">{count}</span>

        {!locked && (
          <button
            type="button"
            className={`rc-icon-button is-toggle${isGroupVisible ? '' : ' is-off'}`}
            aria-label={isGroupVisible ? `Hide ${groupLabel(group)}` : `Show ${groupLabel(group)}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility(group, 'group');
            }}
          >
            {isGroupVisible ? <VisibilityOutlinedIcon /> : <VisibilityOffOutlinedIcon />}
          </button>
        )}
      </div>

      <div className="rc-group-items">{children}</div>
    </div>
  );
}

function DroppableDeleteZone({ isDragging }) {
  const { setNodeRef, isOver } = useDroppable({ id: DELETE_ZONE_ID });

  return (
    <div
      className={`rc-dropzone${isDragging ? ' is-armed' : ''}`}
      ref={isDragging ? setNodeRef : null}
      aria-hidden={!isDragging}
    >
      <div className={`rc-dropzone-inner${isOver ? ' is-over' : ''}`}>
        <TrashClosedIcon className="rc-trash-closed" />
        <TrashOpenIcon className="rc-trash-open" />
        Drop to delete
      </div>
    </div>
  );
}

export default function ReportComponents(props) {
  const [tempComponents, setTempComponents] = useState(
    props.components.map((comp) => ({ ...comp }))
  );
  const [createComponentActive, setCreateComponentActive] = useState(false);
  const [groupToggle, setGroupToggle] = useState(props.components.length > 2 ? 'existing' : 'new');
  const [createComponent, setCreateComponent] = useState(() => ({
    id: generateTempComponentId(),
    name: 'New Component',
    group: '',
    position: 1,
  }));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editComponentName, setEditComponentName] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const prevSyncedRef = useRef({});

  useEffect(() => {
    if (props.activeComponentId == null) return undefined;
    const item = document.querySelector(`.rc-item[data-id="${props.activeComponentId}"]`);
    if (!item) return undefined;

    // Scroll only the sidebar's own list. scrollIntoView also scrolls every
    // ancestor, which in Firefox pulls the report body back up at its bottom.
    let list = item.parentElement;
    while (list && !/(auto|scroll)/.test(getComputedStyle(list).overflowY)) {
      list = list.parentElement;
    }
    if (!list) return undefined;

    const itemRect = item.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (itemRect.top < listRect.top) {
      list.scrollTop += itemRect.top - listRect.top;
    } else if (itemRect.bottom > listRect.bottom) {
      list.scrollTop += itemRect.bottom - listRect.bottom;
    }
    return undefined;
  }, [props.activeComponentId]);

  // Sync incoming props into local state.
  useEffect(() => {
    setTempComponents(props.components.map((comp) => ({ ...comp })));
  }, [props.components]);

  // Show the check when a row goes unsynced → synced. Initial load is skipped
  // because prevSyncedRef starts empty, so the first true is not a transition.
  useEffect(() => {
    const prev = prevSyncedRef.current;
    const next = {};
    const newlySaved = [];

    tempComponents.forEach((component) => {
      const id = component.id;
      next[id] = !!component.synced;
      if (prev[id] === false && component.synced) newlySaved.push(id);
    });

    prevSyncedRef.current = next;

    if (newlySaved.length === 0) return;

    setShowSavedIndicator((ids) => [...ids, ...newlySaved]);
    setTimeout(() => {
      setShowSavedIndicator((ids) => ids.filter((id) => !newlySaved.includes(id)));
    }, 2000);
  }, [tempComponents]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    })
  );

  const grouped = useMemo(() => Object.fromEntries(
    groupByPosition(tempComponents.filter(isUserEditableComponent), undefined, (c) => c.group || UNGROUPED)
  ), [tempComponents]);

  const groupNames = useMemo(() => Object.keys(grouped), [grouped]);
  const groupSortableIds = useMemo(() => groupNames.map(groupSortableId), [groupNames]);
  const componentSortableIdsByGroup = useMemo(() => {
    const ids = {};
    for (const group of groupNames) {
      ids[group] = grouped[group].map((c) => compSortableId(c.id));
    }
    return ids;
  }, [groupNames, grouped]);

  const existingGroups = useMemo(() => [
    ...new Set(
      tempComponents
        .map((c) => c.group)
        .filter((g) => typeof g === 'string' && g.length > 0)
    ),
  ], [tempComponents]);

  const componentGroupIsVisible = (group) => {
    const groupComps = props.components.filter((c) => c.group === group);
    if (groupComps.length < 1 || !props.hiddenComponents?.length) return true;
    return !groupComps.every((c) => props.hiddenComponents.includes(`${c.id}`));
  };

  const componentIsVisible = (component) => {
    if (!props.hiddenComponents?.length) return true;
    return !props.hiddenComponents.includes(`${component.id}`);
  };

  const hiddenCount = useMemo(() => (
    tempComponents
      .filter(isUserEditableComponent)
      .filter((c) => !componentIsVisible(c))
      .length
  ), [tempComponents, props.hiddenComponents]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNameChange = (id, name) => {
    const updated = [...tempComponents];
    const target = updated.find((c) => c.id === id);
    if (target) target.name = name;
    setTempComponents(updated);
  };

  const submitNameChange = () => {
    props.onUpdate(compareComponents(tempComponents));
    setEditComponentName(false);
  };

  const cancelNameChange = (id) => {
    const original = props.components.find((c) => c.id === id);
    if (original) handleNameChange(id, original.name);
    setEditComponentName(false);
  };

  const compareComponents = (components) =>
    components.filter((tempComp) => {
      const originalComp = props.components.find((c) => c.id === tempComp.id);
      if (!originalComp) return true;
      return (
        tempComp.deleted ||
        tempComp.position !== originalComp.position ||
        tempComp.group !== originalComp.group ||
        tempComp.name !== originalComp.name
      );
    });

  // Stamp 1..n in visual order and save every user-editable component so
  // the server never keeps a mix of nulls, duplicates, or holes.
  const reindexAndPersist = (orderedEditable, deleted = []) => {
    const repositioned = orderedEditable.map((c, i) => ({
      ...c,
      position: i + 1,
    }));
    const reservedComps = tempComponents.filter((c) => !isUserEditableComponent(c));

    setTempComponents([...reservedComps, ...repositioned]);
    props.onUpdate([...repositioned, ...deleted]);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Drop on the delete zone -> open the confirm prompt.
    if (over.id === DELETE_ZONE_ID) {
      const parsed = parseSortableId(active.id);
      if (parsed?.type === 'component') {
        const c = tempComponents.find((c) => c.id === parsed.id);
        if (c) setConfirmDelete({ id: c.id, name: c.name, type: 'component' });
      } else if (parsed?.type === 'group') {
        setConfirmDelete({ id: parsed.group, name: parsed.group, type: 'group' });
      }
      return;
    }

    if (active.id === over.id) return;

    const activeParsed = parseSortableId(active.id);
    const overParsed = parseSortableId(over.id);

    if (activeParsed?.type === 'component') {
      const activeComp = tempComponents.find((c) => c.id === activeParsed.id);
      if (!activeComp) return;

      const sorted = orderByPosition(
        tempComponents.filter(isUserEditableComponent)
      );

      const activeIdx = sorted.findIndex((c) => c.id === activeComp.id);
      if (activeIdx === -1) return;

      let overIdx;
      let targetGroup = activeComp.group;

      if (overParsed?.type === 'component') {
        overIdx = sorted.findIndex((c) => c.id === overParsed.id);
        if (overIdx === -1) return;
        targetGroup = sorted[overIdx].group;
      } else if (overParsed?.type === 'group') {
        targetGroup = overParsed.group;
        // Drop at the end of the target group.
        const groupComps = sorted.filter(
          (c) => c.group === targetGroup && c.id !== activeComp.id
        );
        if (groupComps.length === 0) {
          overIdx = sorted.length - 1;
        } else {
          overIdx = sorted.indexOf(groupComps[groupComps.length - 1]);
        }
      } else {
        return;
      }

      const reordered = arrayMove(sorted, activeIdx, overIdx).map((c) => ({
        ...c,
        group: c.id === activeComp.id ? targetGroup : c.group,
      }));

      reindexAndPersist(reordered);
    } else if (activeParsed?.type === 'group' && overParsed?.type === 'group') {
      const activeGroup = activeParsed.group;
      const overGroup = overParsed.group;
      if (activeGroup === overGroup) return;

      const fromIdx = groupNames.indexOf(activeGroup);
      const toIdx = groupNames.indexOf(overGroup);
      if (fromIdx === -1 || toIdx === -1) return;

      const reorderedNames = arrayMove(groupNames, fromIdx, toIdx);
      reindexAndPersist(reorderedNames.flatMap((g) => grouped[g]));
    }
  };

  const initCreateComponent = () => {
    const sortedComponents = orderByPosition(
      tempComponents.filter((c) => c?.group?.length > 0)
    );
    const lastComp = sortedComponents[sortedComponents.length - 1];
    setCreateComponent({
      id: generateTempComponentId(),
      name: 'New Component',
      group: lastComp?.group,
      position: (lastComp?.position || 0) + 1,
    });
    setCreateComponentActive(true);
  };

  const setCreateComponentGroup = (group) => {
    const groupComps = orderByPosition(
      tempComponents.filter((c) => c.group === group)
    );
    const lastInGroup = groupComps[groupComps.length - 1];
    const position = group ? (lastInGroup ? lastInGroup.position + 1 : 1) : 1;
    setCreateComponent({ ...createComponent, group, position });
  };

  const handleCreateComponent = () => {
    const inserted = [
      ...tempComponents.filter(isUserEditableComponent),
      { ...createComponent },
    ];

    reindexAndPersist(orderByPosition(inserted));
    setCreateComponentActive(false);
  };

  const handleDeleteComponent = () => {
    const { id, type } = confirmDelete;
    const deleted = [];
    const remaining = [];

    tempComponents.filter(isUserEditableComponent).forEach((c) => {
      const removing = type === 'component' ? c.id === id : c.group === id;
      if (removing) deleted.push({ ...c, deleted: true });
      else remaining.push(c);
    });

    reindexAndPersist(orderByPosition(remaining), deleted);
    setConfirmDelete(null);
  };

  const activeComponent = useMemo(() => {
    if (!activeId) return null;
    const parsed = parseSortableId(activeId);
    if (parsed?.type === 'component') {
      return tempComponents.find((c) => c.id === parsed.id) || null;
    }
    return null;
  }, [activeId, tempComponents]);

  const activeGroupName = useMemo(() => {
    if (!activeId) return null;
    const parsed = parseSortableId(activeId);
    return parsed?.type === 'group' ? parsed.group : null;
  }, [activeId]);

  const isDragging = activeId !== null;
  const canCreate = createComponent.name?.trim().length > 0;

  return (
    <div className={`components-container${isDragging ? ' is-dragging' : ''}${props.locked ? ' is-locked' : ''}`}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="rc-head">
          <span className="rc-head-title">Systems</span>
          {hiddenCount > 0 ? (
            <span className="rc-head-hidden">{hiddenCount} hidden</span>
          ) : null}
        </div>

        <div className="rc-tree">
          <SortableContext
            items={groupSortableIds}
            strategy={verticalListSortingStrategy}
          >
            {groupNames.map((group) => {
              const components = grouped[group];
              return (
                <SortableGroupContainer
                  key={group}
                  group={group}
                  count={components.length}
                  firstComponentId={components[0]?.id}
                  isGroupVisible={componentGroupIsVisible(group)}
                  isActiveSelection={components.some((c) => `${c.id}` === `${props.activeComponentId}`)}
                  toggleVisibility={props.updateHiddenComponents}
                  onSelectComponent={props.onSelectComponent}
                  locked={props.locked}
                >
                  <SortableContext
                    items={componentSortableIdsByGroup[group]}
                    strategy={verticalListSortingStrategy}
                  >
                    {components.map((component) => (
                      <SortableComponentItem
                        key={component.id}
                        component={component}
                        isVisible={componentIsVisible(component)}
                        isActiveSelection={`${component.id}` === `${props.activeComponentId}`}
                        showSavedIndicator={showSavedIndicator.includes(component.id)}
                        editComponentName={editComponentName}
                        setEditComponentName={setEditComponentName}
                        setConfirmDelete={setConfirmDelete}
                        handleNameChange={handleNameChange}
                        submitNameChange={submitNameChange}
                        cancelNameChange={cancelNameChange}
                        toggleVisibility={props.updateHiddenComponents}
                        onSelectComponent={props.onSelectComponent}
                        locked={props.locked}
                      />
                    ))}
                  </SortableContext>
                </SortableGroupContainer>
              );
            })}
          </SortableContext>

          {confirmDelete || props.locked ? null : (
            <button
              type="button"
              className="rc-create"
              onClick={() => initCreateComponent()}
            >
              <AddRoundedIcon />
              New component
            </button>
          )}
        </div>

        {confirmDelete ? (
          <div
            className="rc-confirm-layer"
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmDelete(null);
            }}
          >
            <div className="rc-confirm" role="alertdialog" aria-label="Confirm remove">
                <span className="rc-confirm-text">
                  Remove {confirmDelete.type === 'group' ? 'group' : ''}{' '}
                  <strong>{confirmDelete.name || confirmDelete.id}</strong> from this report?
                </span>
                <span className="rc-confirm-note">Its readings and notes on this report are deleted. Other reports and the account's systems are not affected.</span>

                <div className="rc-confirm-actions">
                  <button
                    type="button"
                    className="rc-button ghost"
                    onClick={() => setConfirmDelete(null)}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      setConfirmDelete(null);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rc-button danger"
                    onClick={() => handleDeleteComponent()}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      handleDeleteComponent();
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
          </div>
        ) : null}

        {!props.locked && (
          <div className={`rc-footer${isDragging ? ' is-dragging' : ''}`}>
            <DroppableDeleteZone isDragging={isDragging} />
          </div>
        )}

        <DragOverlay>
          {activeComponent && (
            <div className="rc-item drag-overlay">
              <span className="rc-handle">
                <DragIndicatorIcon />
              </span>
              <span className="rc-item-name">{activeComponent.name}</span>
            </div>
          )}
          {activeGroupName && (
            <div className="rc-group drag-overlay">
              <div className="rc-group-head">
                <span className="rc-handle">
                  <DragIndicatorIcon />
                </span>
                <span className="rc-group-name">{groupLabel(activeGroupName)}</span>
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {createComponentActive && (
        <div
          className="rc-modal-scrim"
          onClick={() => setCreateComponentActive(false)}
        >
          <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rc-modal-head">
              <span className="rc-modal-title">New component</span>
              <button
                type="button"
                className="rc-modal-close"
                aria-label="Close"
                onClick={() => setCreateComponentActive(false)}
              >
                <CloseRoundedIcon />
              </button>
            </div>

            <div className="rc-modal-body">
              <label className="rc-field">
                <span className="rc-field-label">Name</span>
                <input
                  className="rc-input browser-default"
                  type="text"
                  autoFocus
                  value={createComponent.name}
                  onChange={(e) =>
                    setCreateComponent({ ...createComponent, name: e.target.value })
                  }
                />
              </label>

              {props.components.length > 2 ? (
                <div className="rc-segmented" role="tablist">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={groupToggle === 'existing'}
                    className={`rc-segment${groupToggle === 'existing' ? ' is-selected' : ''}`}
                    onClick={() => {
                      const lastComp = orderByPosition(
                        tempComponents.filter((c) => c.group)
                      ).slice(-1)[0];
                      setCreateComponentGroup(lastComp?.group);
                      setGroupToggle('existing');
                    }}
                  >
                    Existing group
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={groupToggle === 'new'}
                    className={`rc-segment${groupToggle === 'new' ? ' is-selected' : ''}`}
                    onClick={() => {
                      setCreateComponentGroup('');
                      setGroupToggle('new');
                    }}
                  >
                    New group
                  </button>
                </div>
              ) : null}

              {props.components.length > 2 && groupToggle === 'existing' ? (
                <label className="rc-field">
                  <span className="rc-field-label">Group</span>
                  <select
                    className="rc-input browser-default"
                    value={createComponent.group || ''}
                    onChange={(e) => setCreateComponentGroup(e.target.value)}
                  >
                    {existingGroups.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {groupToggle === 'new' ? (
                <label className="rc-field">
                  <span className="rc-field-label">System group</span>
                  <input
                    className="rc-input browser-default"
                    type="text"
                    placeholder="e.g. Cooling Towers"
                    value={createComponent.group || ''}
                    onChange={(e) =>
                      setCreateComponent({ ...createComponent, group: e.target.value })
                    }
                  />
                </label>
              ) : null}
            </div>

            <div className="rc-modal-foot">
              <button
                type="button"
                className="rc-button ghost"
                onClick={() => setCreateComponentActive(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rc-button primary"
                disabled={!canCreate}
                onClick={() => handleCreateComponent()}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
